import { RouteModule } from '#/horizon/components/RouteModule';
import { getServerRouterContext } from '#/horizon/router-contexts/server-router-context';
import { useI18n } from '#/i18n/hooks/useI18n';
import { I18nLocale } from '#/i18n/types/I18nLocale';
import type { LayoutHandleProps } from '#/layouts/Layout/LayoutHandleProps';
import { searchVolopsRouteReference } from '#/modules/search/routing/search.routes';
import { getSearchPageLinkRels } from '#/modules/search/utils/getSearchPageLinkRels';
import { DynamicSearchVolopsRefinements } from '#/modules/search/volops/components/Refinements/DynamicSearchVolopsRefinements';
import { searchVolopsRoutePathByLocale } from '#/modules/search/volops/routing/seachVolops.route-paths';
import type { HorizonAppRouteLoaderArgs } from '#/routing/classes/types/AppRouteLoaderArgs';
import type { HorizonAppRouteComponentProps } from '#/routing/types/HorizonAppRouteComponentProps';
import { getStaticImageSrc } from '#/utils/getImageSrc';

import { SearchVolopsPage } from './SearchVolopsPage';

export const handle = {
  searchRefinements: (
    <DynamicSearchVolopsRefinements includeSourcesFacet={false} />
  ),
} satisfies LayoutHandleProps;

export function loader({
  url,
  context,
}: HorizonAppRouteLoaderArgs<typeof searchVolopsRouteReference>) {
  const { serverContext } = getServerRouterContext(context);

  return {
    handle: {
      urlByLocale: searchVolopsRoutePathByLocale,
      darkBackground: true,
      defaultSearchType: 'VOLOP',
      hideFooterDivider: true,
      showDesktopSearch: true,
      searchFromPageType: 'SEARCH',
      showSearchRefinements: true,
      isSearchPage: true,
    } satisfies LayoutHandleProps,

    linkRels: getSearchPageLinkRels({
      searchParams: url.searchParams,
      locale: serverContext.locale,
      routePathByLocale: searchVolopsRoutePathByLocale,
      referenceRoute: searchVolopsRouteReference,
    }),

    now: serverContext.now,
  };
}

export default function SearchVolopsHorizonRoute({
  loaderData: { linkRels, now },
}: HorizonAppRouteComponentProps<typeof loader>) {
  const { i18n, locale } = useI18n();

  const ogImage = (() => {
    switch (locale) {
      case I18nLocale.EN:
        return getStaticImageSrc('og-image-en-volunteer.jpg');
      case I18nLocale.ES:
        return;
      case I18nLocale.PT:
        return getStaticImageSrc('og-image-pt-volunteer.jpg');
    }
  })();

  return (
    <RouteModule
      title={
        /* TRANSLATORS: volop search page title */
        i18n(
          'Best Virtual Volunteer Opportunities, On-Site Volunteering, and More',
        )
      }
      description={
        /* TRANSLATORS: volop search page description */
        i18n(
          'Ready to make a difference? Search for volunteer opportunities at nonprofits and purpose-driven companies',
        )
      }
      ogImage={ogImage}
      linkRels={linkRels}
    >
      <SearchVolopsPage now={now} />
    </RouteModule>
  );
}
